/**
 * Transport: batches events and ships them to the collector with retry + offline queue.
 *
 * Design goals (minimal app impact):
 *   - never block the hot path: events go into an in-memory buffer synchronously;
 *   - flush on size threshold, on an interval, and on page hide/unload;
 *   - persist undelivered events to localStorage so a reload/offline doesn't lose them;
 *   - exponential backoff on failure; use sendBeacon on unload for best-effort delivery.
 */
import type { IngestBatch, RequestEvent } from '@aris/request-watcher-shared';
import type { ResolvedConfig } from './config.js';

const SDK = { name: 'sdk-react', version: '0.1.0' };
const STORAGE_KEY = 'aris.rw.queue';

export class Transport {
  private buffer: RequestEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private sending = false;
  private backoff = 0;

  constructor(private cfg: ResolvedConfig) {}

  start(): void {
    this.restoreQueue();
    this.timer = setInterval(() => void this.flush(), this.cfg.flushIntervalMs);
    if (typeof document !== 'undefined') {
      // visibilitychange + pagehide are the reliable unload signals across browsers.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flushBeacon();
      });
      window.addEventListener('pagehide', () => this.flushBeacon());
    }
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  enqueue(ev: RequestEvent): void {
    this.buffer.push(ev);
    if (this.buffer.length >= this.cfg.batchSize) void this.flush();
  }

  /** Async flush with retry/backoff. Safe to call concurrently (guarded). */
  async flush(): Promise<void> {
    if (this.sending || this.buffer.length === 0) return;
    if (this.backoff > Date.now()) return; // in backoff window

    this.sending = true;
    const batch = this.buffer.splice(0, this.cfg.batchSize);
    try {
      await this.send(batch);
      this.backoff = 0;
      this.persistQueue(); // clear persisted copy on success
    } catch (err) {
      // Put events back at the front and schedule backoff.
      this.buffer.unshift(...batch);
      this.trim();
      this.persistQueue();
      this.backoff = Date.now() + this.nextBackoffMs();
      if (this.cfg.debug) console.warn('[aris] flush failed, will retry', err);
    } finally {
      this.sending = false;
    }
  }

  private async send(events: RequestEvent[]): Promise<void> {
    const body: IngestBatch = { sdk: SDK, events };
    const res = await fetch(this.cfg.collectorUrl, {
      method: 'POST',
      // keepalive lets the request outlive a navigating page (small payloads only).
      keepalive: events.length <= 50,
      headers: {
        'content-type': 'application/json',
        'x-aris-api-key': this.cfg.apiKey,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`collector ${res.status}`);
  }

  /** Best-effort synchronous-ish delivery on unload via sendBeacon. */
  private flushBeacon(): void {
    if (this.buffer.length === 0) return;
    const events = this.buffer.splice(0);
    const body: IngestBatch = { sdk: SDK, events };
    let delivered = false;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // sendBeacon can't set custom headers; the collector accepts the api key via query for beacons.
      const url = `${this.cfg.collectorUrl}?api_key=${encodeURIComponent(this.cfg.apiKey)}`;
      delivered = navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: 'application/json' }));
    }
    if (!delivered) {
      this.buffer.unshift(...events);
      this.persistQueue();
    }
  }

  private nextBackoffMs(): number {
    // 1s, 2s, 4s ... capped at 30s, with jitter.
    const base = Math.min(30_000, 1000 * 2 ** Math.min(5, this.attempts++));
    return base + Math.random() * 500;
  }
  private attempts = 0;

  private trim(): void {
    if (this.buffer.length > this.cfg.maxQueueSize) {
      this.buffer.splice(0, this.buffer.length - this.cfg.maxQueueSize);
    }
  }

  // ── offline persistence ──────────────────────────────────
  private persistQueue(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      if (this.buffer.length === 0) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, JSON.stringify(this.buffer.slice(-this.cfg.maxQueueSize)));
    } catch {
      /* storage full / disabled — ignore */
    }
  }

  private restoreQueue(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as RequestEvent[];
        this.buffer.unshift(...saved);
        this.trim();
      }
    } catch {
      /* ignore corrupt queue */
    }
  }
}
